import { CurrencyType } from '@shared/enums/CurrencyType';
import { IntegrationCategoryType } from '@shared/enums/IntegrationCategoryType';
import { IntegrationStatusType } from '@shared/enums/IntegrationStatusType';
import { IntegrationType } from '@shared/enums/IntegrationType';
import { PaymentProviderType } from '@shared/enums/PaymentProviderType';
import { IProfitCenterStatus } from '../halito-service/halito-model';
import { PublicEventSsoAttribute } from '@shared/enums/PublicEventSsoAttribute';
import { PublicEventSsoMode } from '@shared/enums/PublicEventSsoMode';
import { PublicEventSsoAuthType } from '@shared/enums/PublicEventSsoAuthType';

export interface IIntegrationKey {
    apiKey: string;
}

export class IntegrationKey implements IIntegrationKey {
    constructor(
        public apiKey: string,
    ) {
    }
}

export interface IIntegrationCode {
    code: string;
}

export class IntegrationCode implements IIntegrationCode {
    constructor(
        public code: string,
    ) {
    }
}

export interface IIntegrationValidation {
    result: boolean;
}

export interface IIntegrationAuthorization {
    value: string;
}

export interface IIntegration {
    name: IntegrationType;
    type: IntegrationCategoryType;
    documentation: string;
    status: IntegrationStatusType;
    errorCode: string;
}

export class Integration implements IIntegration {
    constructor(
        public name: IntegrationType,
        public type: IntegrationCategoryType,
        public documentation: string,
        public status: IntegrationStatusType,
        public errorCode: string,
    ) {
    }
}

export interface IIntegrationStatusPayment {
    paymentProviderType: PaymentProviderType;
    active: boolean;
    profitCenterStatus?: IProfitCenterStatus;
}

export class IntegrationStatusPayment implements IIntegrationStatusPayment {
    constructor(
        public paymentProviderType: PaymentProviderType,
        public active: boolean,
        public profitCenterStatus?: IProfitCenterStatus,
    ) {
    }
}

export interface IPaymentChargeAmount {
    paymentProviderType: PaymentProviderType;
    currencyType: CurrencyType;
    min: number;
    max: number;
}

export class PaymentChargeAmount implements IPaymentChargeAmount {
    constructor(
        public paymentProviderType: PaymentProviderType,
        public currencyType: CurrencyType,
        public min: number,
        public max: number,
    ) {
    }
}

export interface IConnectorValidations {
    crms: IConnectorValidation[];
}

export class ConnectorValidations implements IConnectorValidations {
    constructor(
        public crms: IConnectorValidation[],
    ) {
    }
}

export interface IConnectorValidation {
    sourceCrm: string;
    destinationCrm: string;
    tenantName: string;
    syncEnabled: boolean;
}

export class ConnectorValidation implements IConnectorValidation {
    constructor(
        public sourceCrm: string,
        public destinationCrm: string,
        public tenantName: string,
        public syncEnabled: boolean,
    ) {
    }
}

export interface IZoomWebinars {
    webinars: IZoomWebinar[];
}

export class ZoomWebinars implements IZoomWebinars {
    constructor(
        public webinars: IZoomWebinar[],
    ) {
    }
}

export interface IZoomWebinar {
    id: string;
    name: string;
    startTime: Date;
    timeZone: string;
    duration: number;
}

export class ZoomWebinar implements IZoomWebinar {
    constructor(
        public id: string,
        public name: string,
        public startTime: Date,
        public timeZone: string,
        public duration: number,
    ) {
    }
}

export interface IPublicEventSsoAttributeMappingModel {
  attribute: PublicEventSsoAttribute;
  path: string;
}

export interface IPublicEventSsoGetItem {
  // Credentials (Key Vault) - never return secrets
  clientId: string;
  hasClientSecret: boolean;
  clientSecretMasked: string;

  basicUsername: string;
  hasBasicPassword: boolean;
  basicPasswordMasked: string;

  // OAuth2 (Client Credentials) settings (DB)
  oAuthTokenEndpointUrl: string;
  oAuthScope: string;

  // Endpoints (DB)
  endpointUrl: string;
  healthCheckUrl: string;

  // Default language ID (DB)
  defaultLanguageId: string;

  // Mode (DB)
  publicEventSsoMode: PublicEventSsoMode;

  // Auth (DB)
  publicEventSsoAuthType: PublicEventSsoAuthType;

  // Mappings (DB)
  attributeMappings: IPublicEventSsoAttributeMappingModel[];
}

export interface IPublicEventSsoUpsertRequest {
  // Credentials (Key Vault) - optional/required depending on auth type
  clientId: string;
  clientSecret: string;

  basicUsername: string;
  basicPassword: string;

  // OAuth2 (Client Credentials) settings (DB)
  oAuthTokenEndpointUrl: string;
  oAuthScope: string;

  // Endpoints (DB)
  endpointUrl: string;
  healthCheckUrl: string;

  // Default language ID (DB)
  defaultLanguageId: string;

  // Mode (DB)
  publicEventSsoMode: PublicEventSsoMode;

  // Auth (DB)
  publicEventSsoAuthType: PublicEventSsoAuthType;

  // Mappings (DB)
  attributeMappings: IPublicEventSsoAttributeMappingModel[];
}

export interface IPublicEventSsoValidateItem {
  exists: boolean;
  result: boolean;
}
